import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { supabase, loadAppSettings, updateAppSetting, loadInstitutionalOwners } from "../../supabase";
import PPIcon from "./PPIcon";
import { useUserProfile } from "../../auth/UserProfileContext";
import MsaManagerPanel from "./MsaManagerPanel";
import BuyBoxManagerPanel from "./BuyBoxManagerPanel";

export default function SettingsPage({ onBack }) {
  const { accounts } = useMsal();
  const account = accounts[0];
  const { profile } = useUserProfile();
  const [loiSettings, setLoiSettings] = useState({ cooldown_days: '14', deposit_pct: '0.01', offer_expiry_days: '16', dd_period: '30 Business Days' });
  const [instOwners, setInstOwners] = useState([]);
  const [newOwnerName, setNewOwnerName] = useState('');
  const [settingsToast, setSettingsToast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [settingsRes, instRes] = await Promise.all([
          supabase.from('app_settings').select('*'),
          supabase.from('institutional_owners').select('*').order('name'),
        ]);
        const settings = {};
        (settingsRes.data || []).forEach(r => { settings[r.key] = r.value; });

        setLoiSettings({
          cooldown_days: settings.cooldown_days || '14',
          deposit_pct: settings.deposit_pct || '0.01',
          offer_expiry_days: settings.offer_expiry_days || '16',
          dd_period: settings.dd_period || '30 Business Days',
        });
        setInstOwners(instRes.data || []);
      } catch (err) {
        console.error("Settings load error:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const showSaved = () => {
    setSettingsToast('Saved');
    setTimeout(() => setSettingsToast(null), 2000);
  };

  const saveLoiSetting = async (key, value) => {
    setLoiSettings(prev => ({ ...prev, [key]: value }));
    await updateAppSetting(key, value);
    showSaved();
  };

  const addInstOwner = async () => {
    const name = newOwnerName.trim();
    if (!name) return;
    const { data, error } = await supabase.from('institutional_owners').insert({ name }).select().single();
    if (!error && data) {
      setInstOwners(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewOwnerName('');
      showSaved();
    }
  };

  const deleteInstOwner = async (id) => {
    await supabase.from('institutional_owners').delete().eq('id', id);
    setInstOwners(prev => prev.filter(o => o.id !== id));
    showSaved();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="flex gap-1.5">
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    );
  }

  const inputCls = "w-full bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ring-[#e93529]";

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Toast */}
      {settingsToast && (
        <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">
          {settingsToast} ✓
        </div>
      )}

      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-1.5 text-[#8a8a8a] hover:text-white transition-colors text-sm mb-6">
        ← Back
      </button>

      <h1 className="text-2xl font-bold text-white font-montserrat mb-8">Settings</h1>

      {/* ── 1. Signed-in User ── */}
      <div className="bg-[#2c2c2c] rounded-xl p-6 mb-6">
        <h2 className="text-lg font-bold text-white mb-1 font-montserrat">Account</h2>
        <p className="text-sm text-[#8a8a8a] mb-4">LOIs are sent from and attributed to the signed-in user. Contact info is pulled from Microsoft 365 — update it there to change your LOI signature.</p>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-pp-red text-white font-bold flex items-center justify-center shrink-0">
            {(profile?.name || account?.name || account?.username || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-white">
              {profile?.name || account?.name || '—'}
              {profile?.jobTitle && <span className="ml-2 text-[#8a8a8a] font-normal">— {profile.jobTitle}</span>}
            </p>
            <p className="text-xs text-[#8a8a8a]">{profile?.email || account?.username || ''}</p>
            {profile?.phone && <p className="text-xs text-[#8a8a8a]">{profile.phone}</p>}
          </div>
        </div>
      </div>

      {/* ── 3. LOI Configuration ── */}
      <div className="bg-[#2c2c2c] rounded-xl p-6 mb-6">
        <h2 className="text-lg font-bold text-white mb-1 font-montserrat">LOI Configuration</h2>
        <p className="text-sm text-[#8a8a8a] mb-4">Settings that control LOI generation and outreach timing</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-[#8a8a8a] mb-1 block">Cooldown Period (days)</label>
            <input type="number" value={loiSettings.cooldown_days} onChange={e => setLoiSettings(prev => ({ ...prev, cooldown_days: e.target.value }))} onBlur={e => saveLoiSetting('cooldown_days', e.target.value)} className={inputCls} />
            <p className="text-xs text-[#8a8a8a] mt-1">Days before a property is eligible for another LOI</p>
          </div>
          <div>
            <label className="text-sm text-[#8a8a8a] mb-1 block">Deposit Percentage</label>
            <input type="number" step="0.001" value={loiSettings.deposit_pct} onChange={e => setLoiSettings(prev => ({ ...prev, deposit_pct: e.target.value }))} onBlur={e => saveLoiSetting('deposit_pct', e.target.value)} className={inputCls} />
            <p className="text-xs text-[#8a8a8a] mt-1">e.g., 0.01 = 1% of total offer</p>
          </div>
          <div>
            <label className="text-sm text-[#8a8a8a] mb-1 block">Offer Expiration (days)</label>
            <input type="number" value={loiSettings.offer_expiry_days} onChange={e => setLoiSettings(prev => ({ ...prev, offer_expiry_days: e.target.value }))} onBlur={e => saveLoiSetting('offer_expiry_days', e.target.value)} className={inputCls} />
            <p className="text-xs text-[#8a8a8a] mt-1">Days until LOI offer expires</p>
          </div>
          <div>
            <label className="text-sm text-[#8a8a8a] mb-1 block">Due Diligence Period</label>
            <input type="text" value={loiSettings.dd_period} onChange={e => setLoiSettings(prev => ({ ...prev, dd_period: e.target.value }))} onBlur={e => saveLoiSetting('dd_period', e.target.value)} className={inputCls} />
            <p className="text-xs text-[#8a8a8a] mt-1">Text shown in LOIs</p>
          </div>
        </div>
      </div>

      {/* ── 4. MSAs & Counties ── */}
      <div className="mb-6">
        <MsaManagerPanel />
      </div>

      {/* ── 4b. Buy-Box Rule ── */}
      <div className="mb-6">
        <BuyBoxManagerPanel />
      </div>

      {/* ── 5. Institutional Owners ── */}
      <div className="bg-[#2c2c2c] rounded-xl p-6 mb-6">
        <h2 className="text-lg font-bold text-white mb-1 font-montserrat">Institutional Owner List</h2>
        <p className="text-sm text-[#8a8a8a] mb-4">Properties owned by these companies are flagged as institutional and separated in Sourcing</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {instOwners.map(o => (
            <span key={o.id} className="inline-flex items-center gap-1.5 bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-3 py-1 text-sm">
              {o.name}
              <button onClick={() => deleteInstOwner(o.id)} className="text-[#8a8a8a] hover:text-[#f04f44] transition-colors text-xs leading-none">✕</button>
            </span>
          ))}
          {instOwners.length === 0 && <p className="text-[#8a8a8a] text-sm">No institutional owners defined.</p>}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={newOwnerName}
            onChange={e => setNewOwnerName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addInstOwner(); }}
            placeholder="Type owner name and press Enter"
            className={`${inputCls} flex-1`}
          />
          <button onClick={addInstOwner} className="bg-[#c42d22] hover:bg-[#e93529] text-white text-sm px-4 py-2 rounded-lg transition-colors">Add</button>
        </div>
      </div>

      {/* ── 5. About ── */}
      <div className="bg-[#2c2c2c] rounded-xl p-6 mb-6">
        <h2 className="text-lg font-bold text-white mb-1 font-montserrat">About Proxy</h2>
        <div className="flex items-center gap-3 mt-3">
          <PPIcon size={24} />
          <div>
            <p className="text-sm text-white font-semibold">Proxy v1.0</p>
            <p className="text-xs text-[#8a8a8a]">Built by ProximityParks USA</p>
          </div>
        </div>
        <a href="https://github.com/PPUSAproxy/Proxy" target="_blank" rel="noopener noreferrer" className="text-sm text-[#f04f44] hover:text-[#f04f44] mt-3 inline-block">github.com/PPUSAproxy/Proxy →</a>
      </div>
    </div>
  );
}

